import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon } from '../primitives/Icon';

export interface Crumb {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: Crumb[];
  /** Collapse the middle when the trail is long, keeping first and last two. */
  maxVisible?: number;
}

export const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(function Breadcrumbs(
  { items, maxVisible = 5, className, ...rest },
  ref,
) {
  const visible =
    items.length > maxVisible
      ? [items[0]!, { label: '…' } as Crumb, ...items.slice(items.length - 2)]
      : items;

  return (
    <nav ref={ref} aria-label="Breadcrumb" className={cn('min-w-0', className)} {...rest}>
      <ol className="flex flex-wrap items-center gap-1 text-dense-lg text-content-secondary">
        {visible.map((crumb, index) => {
          const isLast = index === visible.length - 1;
          const isEllipsis = crumb.label === '…' && !crumb.href && !crumb.onClick;
          return (
            <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
              {index > 0 ? (
                <Icon name="chevron-right" size={13} className="text-content-muted" />
              ) : null}
              {isLast ? (
                <span aria-current="page" className="truncate font-medium text-content">
                  {crumb.label}
                </span>
              ) : isEllipsis ? (
                <span aria-hidden="true">…</span>
              ) : crumb.href ? (
                <a
                  href={crumb.href}
                  className={cn('rounded-sm text-content-link hover:underline', focusRing)}
                >
                  {crumb.label}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={crumb.onClick}
                  className={cn('rounded-sm text-content-link hover:underline', focusRing)}
                >
                  {crumb.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});
